/**
 * API Client
 * 
 * Axios instance configured for the GPU Platform API.
 */

import axios from 'axios';
import toast from 'react-hot-toast';
import { startSsoLogin } from '../utils/ssoAuth';

// Handle empty string for relative paths (when behind nginx proxy)
// If VITE_API_URL is explicitly set to empty string, use it (for relative paths)
// Otherwise, use the default localhost URL for development
const API_URL = import.meta.env.VITE_API_URL !== undefined 
  ? import.meta.env.VITE_API_URL 
  : 'http://localhost:3001';

export const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      // Handle authentication errors (skip redirect on public auth endpoints — wrong password / OTP must not hard-redirect)
      if (status === 401) {
        const reqUrl = (error.config?.url ?? '') as string;
        const isSsoCallback = reqUrl.includes('/auth/sso/callback');
        if (!isSsoCallback) {
          localStorage.removeItem('gpu-platform-auth');
          const returnTo = window.location.pathname + window.location.search;
          startSsoLogin(returnTo);
        }
        return Promise.reject(error);
      }

      // Handle validation errors
      if (status === 400) {
        const message = data.message || 'Invalid request';
        toast.error(message);
      }

      // Handle forbidden errors
      if (status === 403) {
        const message = data.message || 'Access denied';
        toast.error(message);
      }

      // Handle server errors
      if (status >= 500) {
        toast.error('Server error. Please try again later.');
      }
    } else if (error.request) {
      // Network error
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  },
);

// GPU Job API types
export interface GpuJob {
  id: string;
  userId: string;
  userEmail: string;
  instanceType: string;
  gpuCount: number;
  useSpot: boolean;
  maxRuntimeHours: number;
  project: string;
  environment: string;
  /** Compute type: ec2 for EC2 instances, ecs for ECS tasks */
  computeType: 'ec2' | 'ecs';
  status: 'pending' | 'provisioning' | 'running' | 'stopping' | 'stopped' | 'completed' | 'failed' | 'cancelled' | 'terminated';
  instanceId?: string;
  privateIpAddress?: string;
  publicIpAddress?: string;
  estimatedCostUsd: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  /** SSH key pair name in AWS */
  sshKeyName?: string;
  /** SSH private key (only available once after job creation) */
  sshPrivateKey?: string;
  /** Dedicated VPC ID for this instance */
  vpcId?: string;
  /** Dedicated Subnet ID for this instance */
  subnetId?: string;
  /** Dedicated Security Group ID for this instance */
  securityGroupId?: string;
  /** Whether this instance uses a dedicated/isolated VPC */
  isDedicatedNetwork?: boolean;
  /** Advanced configuration options */
  advancedOptions?: AdvancedJobOptions;
}

/**
 * Response from cancelling a GPU job with network cleanup info
 */
export interface GpuJobCancelResponse extends GpuJob {
  networkCleanup?: {
    attempted: boolean;
    vpcId?: string;
    success: boolean;
    message: string;
    sharedWithOtherInstances?: boolean;
  };
}

/**
 * Advanced options for GPU job creation
 */
export interface AdvancedJobOptions {
  // Storage
  rootVolumeSize?: number;
  rootVolumeType?: 'gp3' | 'gp2' | 'io1' | 'io2';
  useInstanceStore?: boolean;

  // Networking
  assignPublicIp?: boolean;
  /** Use dedicated/isolated VPC for this instance instead of shared VPC */
  useDedicatedNetwork?: boolean;
  /** Network mode: shared (default platform VPC), dedicated (isolated VPC), or existing (select existing network) */
  networkMode?: 'shared' | 'dedicated' | 'existing';
  /** VPC ID when using existing network */
  existingVpcId?: string;
  /** Subnet ID when using existing network */
  existingSubnetId?: string;
  /** Security Group ID when using existing network */
  existingSecurityGroupId?: string;
  availabilityZone?: string;
  placementGroupName?: string;

  // Spot
  maxSpotPriceUsd?: number;
  spotInterruptionBehavior?: 'terminate' | 'stop' | 'hibernate';
  spotFallbackToOnDemand?: boolean;

  // Container
  dockerImage?: string;
  containerEnv?: Record<string, string>;
  startupCommand?: string;
  s3Mounts?: string[];
  efsFileSystemId?: string;

  // Lifecycle
  idleTimeoutMinutes?: number;
  scheduledStartTime?: string;
  requireHeartbeat?: boolean;
  priority?: 'low' | 'normal' | 'high';

  // Notifications
  webhookUrl?: string;
  notifyOnComplete?: boolean;
  notifyOnFailure?: boolean;
  snsTopicArn?: string;

  // Resource limits
  maxCostUsd?: number;
  gpuMemoryLimit?: number;
  cpuLimit?: number;
  memoryLimitGb?: number;

  // Metadata
  customTags?: Record<string, string>;
  description?: string;
  name?: string;

  // Security
  instanceProfileArn?: string;
  additionalSecurityGroupIds?: string[];
  sshKeyMode?: 'generate' | 'none' | 'existing';
  existingSshKeyName?: string;
}

export type NetworkMode = 'shared' | 'dedicated' | 'existing';

export interface CreateJobRequest {
  instanceType: string;
  useSpot?: boolean;
  maxRuntimeHours?: number;
  project?: string;
  environment?: 'development' | 'staging' | 'production';
  
  // Advanced options (flattened for API compatibility)
  rootVolumeSize?: number;
  rootVolumeType?: 'gp3' | 'gp2' | 'io1' | 'io2';
  useInstanceStore?: boolean;
  assignPublicIp?: boolean;
  /** Use dedicated/isolated VPC for this instance instead of shared VPC */
  useDedicatedNetwork?: boolean;
  /** Network mode: shared (default platform VPC), dedicated (isolated VPC), or existing (select existing network) */
  networkMode?: NetworkMode;
  /** VPC ID when using existing network */
  existingVpcId?: string;
  /** Subnet ID when using existing network */
  existingSubnetId?: string;
  /** Security Group ID when using existing network */
  existingSecurityGroupId?: string;
  availabilityZone?: string;
  placementGroupName?: string;
  maxSpotPriceUsd?: number;
  spotInterruptionBehavior?: 'terminate' | 'stop' | 'hibernate';
  spotFallbackToOnDemand?: boolean;
  dockerImage?: string;
  containerEnv?: Record<string, string>;
  startupCommand?: string;
  s3Mounts?: string[];
  efsFileSystemId?: string;
  idleTimeoutMinutes?: number;
  scheduledStartTime?: string;
  requireHeartbeat?: boolean;
  priority?: 'low' | 'normal' | 'high';
  webhookUrl?: string;
  notifyOnComplete?: boolean;
  notifyOnFailure?: boolean;
  snsTopicArn?: string;
  maxCostUsd?: number;
  gpuMemoryLimit?: number;
  cpuLimit?: number;
  memoryLimitGb?: number;
  customTags?: Record<string, string>;
  description?: string;
  name?: string;
  instanceProfileArn?: string;
  additionalSecurityGroupIds?: string[];
  sshKeyMode?: 'generate' | 'none' | 'existing';
  existingSshKeyName?: string;
}

export interface CreateJobResponse {
  jobId: string;
  instanceId?: string;
  status: string;
  estimatedCostUsd: number;
}

export interface CostEstimate {
  instanceType: string;
  gpuCount: number;
  maxRuntimeHours: number;
  useSpot: boolean;
  hourlyRateUsd: number;
  estimatedCostUsd: number;
}

export interface RunningInstance {
  instanceId: string;
  instanceType: string;
  state: string;
  publicIpAddress?: string;
  privateIpAddress?: string;
  launchTime?: string;
  jobId?: string;
  project?: string;
  owner?: string;
  ttl?: string;
}

// Activity Log types
export interface ActivityLog {
  id: string;
  userId: string;
  userEmail: string;
  action: string;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  timestamp: string;
}

export interface ActivityResponse {
  data: ActivityLog[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

export interface ActivitySummary {
  action: string;
  count: number;
}

export interface ActivitySummaryResponse {
  data: ActivitySummary[];
  period: {
    days: number;
    startDate: string;
    endDate: string;
  };
}

/** Detailed network information for a GPU instance */
export interface InstanceNetworkDetails {
  jobId: string;
  instanceId?: string;
  status: string;
  network: {
    vpcId?: string;
    vpcCidr?: string;
    vpcState?: string;
    subnetId?: string;
    subnetCidr?: string;
    subnetAvailabilityZone?: string;
    subnetState?: string;
    securityGroupId?: string;
    securityGroupName?: string;
    internetGatewayId?: string;
    routeTableId?: string;
    inboundRules?: Array<{
      protocol: string;
      fromPort?: number;
      toPort?: number;
      source: string;
      description?: string;
    }>;
    outboundRules?: Array<{
      protocol: string;
      fromPort?: number;
      toPort?: number;
      destination: string;
      description?: string;
    }>;
  };
  isProvisioned: boolean;
  error?: string;
}

// API functions
export const gpuJobsApi = {
  list: () => api.get<GpuJob[]>('/api/v1/gpu-jobs').then((r) => r.data),

  get: (jobId: string) =>
    api.get<GpuJob>(`/api/v1/gpu-jobs/${jobId}`).then((r) => r.data),

  create: (data: CreateJobRequest) =>
    api.post<CreateJobResponse>('/api/v1/gpu-jobs', data).then((r) => r.data),

  cancel: (jobId: string) =>
    api.delete<GpuJobCancelResponse>(`/api/v1/gpu-jobs/${jobId}`, {
      timeout: 180000, // 3 minutes for instance termination and network cleanup
    }).then((r) => r.data),

  /** Permanently delete a job record and all associated resources */
  remove: (jobId: string) =>
    api.delete<{ deleted: boolean; message: string }>(`/api/v1/gpu-jobs/${jobId}/purge`, {
      timeout: 180000, // 3 minutes for network cleanup
    }).then((r) => r.data),

  /** Get detailed network information for a specific GPU instance */
  getNetworkDetails: (jobId: string) =>
    api.get<InstanceNetworkDetails>(`/api/v1/gpu-jobs/${jobId}/network`).then((r) => r.data),

  estimateCost: (params: {
    instanceType: string;
    maxRuntimeHours: number;
    useSpot?: boolean;
  }) =>
    api
      .get<CostEstimate>('/api/v1/gpu-jobs/estimate/cost', { params })
      .then((r) => r.data),

  getRunningInstances: () =>
    api.get<RunningInstance[]>('/api/v1/gpu-jobs/instances').then((r) => r.data),
};

// Activity API functions
export const activityApi = {
  getRecent: (limit = 20, offset = 0) =>
    api
      .get<ActivityResponse>('/api/v1/activity', { params: { limit, offset } })
      .then((r) => r.data),

  getSummary: (days = 7) =>
    api
      .get<ActivitySummaryResponse>('/api/v1/activity/summary', { params: { days } })
      .then((r) => r.data),

  getUserActivity: (userId: string, limit = 20, offset = 0) =>
    api
      .get<ActivityResponse>(`/api/v1/activity/user/${userId}`, { params: { limit, offset } })
      .then((r) => r.data),
};

// API Key types
export type ApiKeyScope = 'jobs:read' | 'jobs:write' | 'jobs:delete' | 'usage:read' | 'keys:read' | 'keys:write';

export interface ClientInfo {
  userId?: string;
  userEmail?: string;
  userName?: string;
  workspaceId?: string;
  workspaceName?: string;
  workspaceSlug?: string;
  gpuContainerId?: string;
  gpuContainerName?: string;
  source?: string;
  createdAt?: string;
}

export interface ApiKey {
  id: string;
  keyPrefix: string;
  name: string;
  scopes: ApiKeyScope[];
  rateLimit: number;
  lastUsedAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  clientInfo?: ClientInfo | null;
  ownerEmail?: string;
}

export interface NewApiKey extends ApiKey {
  /** The full API key - ONLY returned once on creation! */
  key: string;
}

export interface CreateApiKeyRequest {
  name: string;
  scopes: ApiKeyScope[];
  rateLimit?: number;
  expiresAt?: string;
}

// API Keys API functions
export const apiKeysApi = {
  list: () => api.get<ApiKey[]>('/api/v1/api-keys').then((r) => r.data),

  create: (data: CreateApiKeyRequest) =>
    api.post<NewApiKey>('/api/v1/api-keys', data).then((r) => r.data),

  revoke: (keyId: string) =>
    api.delete(`/api/v1/api-keys/${keyId}`).then((r) => r.data),

  delete: (keyId: string) =>
    api.delete(`/api/v1/api-keys/${keyId}/permanent`).then((r) => r.data),
};

// ============================================
// Serverless GPU Functions API
// ============================================

export type BuildStatus = 'pending' | 'building' | 'ready' | 'failed';
export type InvocationStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'timeout';

export interface ServerlessFunction {
  id: string;
  name: string;
  version: string;
  pythonVersion: string;
  gpuType: string;
  timeoutSeconds: number;
  memoryMb: number;
  buildStatus: BuildStatus;
  sourceHash: string;
  description?: string;
  buildLogs?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FunctionInvocation {
  id: string;
  functionId: string;
  status: InvocationStatus;
  instanceType: string;
  workerId?: string;
  queuedAt: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  computeCostUsd?: number;
  errorMessage?: string;
}

export interface CreateFunctionRequest {
  name: string;
  sourceCode: string;
  requirements?: string;
  pythonVersion?: string;
  gpuType?: string;
  timeoutSeconds?: number;
  memoryMb?: number;
  description?: string;
}

// Serverless Functions API
export const serverlessFunctionsApi = {
  list: () => 
    api.get<ServerlessFunction[]>('/api/v1/functions').then((r) => r.data),

  get: (id: string) =>
    api.get<ServerlessFunction>(`/api/v1/functions/${id}`).then((r) => r.data),

  create: (data: CreateFunctionRequest) =>
    api.post<ServerlessFunction>('/api/v1/functions', data).then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/api/v1/functions/${id}`),

  triggerBuild: (id: string) =>
    api.post(`/api/v1/functions/${id}/build`),
};

// Serverless Invocations API
export const serverlessInvocationsApi = {
  list: (functionId?: string, limit = 50) => {
    const params: Record<string, unknown> = { limit };
    if (functionId) params.functionId = functionId;
    return api.get<FunctionInvocation[]>('/api/v1/invocations', { params }).then((r) => r.data);
  },

  get: (id: string) =>
    api.get<FunctionInvocation>(`/api/v1/invocations/${id}`).then((r) => r.data),

  getLogs: (id: string) =>
    api.get<{ logs: string }>(`/api/v1/invocations/${id}/logs`).then((r) => r.data.logs),

  cancel: (id: string) =>
    api.post(`/api/v1/invocations/${id}/cancel`),
};

// ============================================
// Infrastructure Management API
// ============================================

export interface InstanceInfo {
  instanceId: string;
  instanceType: string;
  state: string;
  privateIpAddress?: string;
  publicIpAddress?: string;
  launchTime?: string;
  tags: Record<string, string>;
}

export interface FleetStatus {
  totalInstances: number;
  runningInstances: number;
  stoppedInstances: number;
  pendingInstances: number;
  estimatedHourlyCostUsd: number;
  instances: InstanceInfo[];
}

export interface FleetActionResult {
  action: 'start' | 'stop' | 'terminate';
  success: string[];
  failed: string[];
  message: string;
}

export interface CostSavingsEstimate {
  currentHourlyCostUsd: number;
  dailySavingsUsd: number;
  monthlySavingsUsd: number;
  runningInstances: number;
}

// Infrastructure API functions
export const infrastructureApi = {
  getFleetStatus: () =>
    api.get<FleetStatus>('/api/v1/infrastructure/status').then((r) => r.data),

  getCostSavings: () =>
    api.get<CostSavingsEstimate>('/api/v1/infrastructure/cost-savings').then((r) => r.data),

  startFleet: () =>
    api.post<FleetActionResult>('/api/v1/infrastructure/fleet/start').then((r) => r.data),

  stopFleet: () =>
    api.post<FleetActionResult>('/api/v1/infrastructure/fleet/stop').then((r) => r.data),

  terminateFleet: () =>
    api.post<FleetActionResult>('/api/v1/infrastructure/fleet/terminate').then((r) => r.data),

  startInstance: (instanceId: string) =>
    api.post<{ message: string }>(`/api/v1/infrastructure/instances/${instanceId}/start`).then((r) => r.data),

  stopInstance: (instanceId: string) =>
    api.post<{ message: string }>(`/api/v1/infrastructure/instances/${instanceId}/stop`).then((r) => r.data),

  hibernateInstance: (instanceId: string) =>
    api.post<{ message: string }>(`/api/v1/infrastructure/instances/${instanceId}/hibernate`).then((r) => r.data),

  terminateInstance: (instanceId: string) =>
    api.post<TerminateInstanceResponse>(`/api/v1/infrastructure/instances/${instanceId}/terminate`, {}, {
      timeout: 180000, // 3 minutes for instance termination and network cleanup
    }).then((r) => r.data),
};

/**
 * Response from terminating an instance with automatic network cleanup info
 */
export interface TerminateInstanceResponse {
  message: string;
  instanceId: string;
  terminated: boolean;
  networkCleanup: {
    attempted: boolean;
    vpcId?: string;
    success: boolean;
    message: string;
    sharedWithOtherInstances?: boolean;
  };
}

// ============================================
// Deployment Management API
// ============================================

export type DeploymentStatus = 'not_deployed' | 'deploying' | 'deployed' | 'destroying' | 'failed';
export type InfrastructureType = 'ec2' | 'ecs';

export interface Deployment {
  id: string;
  name: string;
  infrastructureType: InfrastructureType;
  status: DeploymentStatus;
  region?: string;
  vpcId?: string;
  subnetId?: string;
  securityGroupId?: string;
  launchTemplateId?: string;
  ecsClusterArn?: string;
  ecsClusterName?: string;
  terraformOutputs?: Record<string, unknown>;
  lastLogs?: string;
  errorMessage?: string;
  deployedAt?: string;
  destroyedAt?: string;
  deployedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeploymentStatusResponse {
  ec2: Deployment | null;
  ecs: Deployment | null;
}

export interface DeploymentConfig {
  region?: string;
  instanceTypes?: string[];
  useSpot?: boolean;
  budgetLimit?: number;
  clusterName?: string;
  desiredCount?: number;
  maxInstances?: number;
  tags?: Record<string, string>;
  autoScaleDownGracePeriodMs?: number;
  autoScaleDownCheckIntervalMs?: number;
  // Container / task definition
  dockerImage?: string;
  startupCommand?: string;
  environmentVariables?: Record<string, string>;
  containerPort?: number;
  hostPort?: number;
  taskCpu?: string;
  taskMemory?: string;
  // Health check
  healthCheckPath?: string;
  healthCheckIntervalSeconds?: number;
  healthCheckTimeoutSeconds?: number;
  healthCheckRetries?: number;
  // CloudWatch logging
  logGroupName?: string;
  logRetentionDays?: number;
  // Platform lifecycle
  idleTimeoutMinutes?: number;
  requireHeartbeat?: boolean;
  maxCostUsd?: number;
  notifyOnComplete?: boolean;
  notifyOnFailure?: boolean;
}

export interface DeploymentResult {
  id: string;
  status: DeploymentStatus;
  message: string;
  outputs?: Record<string, unknown>;
  logs?: string;
  operationId?: string;
}

// Deployment API functions
export const deploymentApi = {
  getStatus: () =>
    api.get<DeploymentStatusResponse>('/api/v1/infrastructure/deployments').then((r) => r.data),

  list: () =>
    api.get<Deployment[]>('/api/v1/infrastructure/deployments/list').then((r) => r.data),

  get: (id: string) =>
    api.get<Deployment>(`/api/v1/infrastructure/deployments/${id}`).then((r) => r.data),

  deployEc2: (config?: DeploymentConfig) =>
    api.post<DeploymentResult>('/api/v1/infrastructure/deploy/ec2', config || {}).then((r) => r.data),

  deployEcs: (config?: DeploymentConfig) =>
    api.post<DeploymentResult>('/api/v1/infrastructure/deploy/ecs', config || {}).then((r) => r.data),

  destroyEc2: () =>
    api.post<DeploymentResult>('/api/v1/infrastructure/destroy/ec2').then((r) => r.data),

  destroyEcs: () =>
    api.post<DeploymentResult>('/api/v1/infrastructure/destroy/ecs').then((r) => r.data),

  cancelEc2: () =>
    api.post<DeploymentResult>('/api/v1/infrastructure/cancel/ec2').then((r) => r.data),

  cancelEcs: () =>
    api.post<DeploymentResult>('/api/v1/infrastructure/cancel/ecs').then((r) => r.data),
};

// ============================================
// ECS Cluster Management API
// ============================================

export interface EcsClusterInfo {
  clusterArn: string;
  clusterName: string;
  status: string;
  runningTasksCount: number;
  pendingTasksCount: number;
  activeServicesCount: number;
  registeredContainerInstancesCount: number;
  capacityProviders?: string[];
  hasGpuSupport?: boolean;
}

export interface EcsTaskInfo {
  taskArn: string;
  taskDefinitionArn: string;
  clusterArn: string;
  lastStatus: string;
  desiredStatus: string;
  cpu?: string;
  memory?: string;
  startedAt?: string;
  stoppedAt?: string;
  stoppedReason?: string;
  containerInstanceArn?: string;
  launchType?: string;
  platformVersion?: string;
  group?: string;
  tags: Record<string, string>;
}

export interface EcsServiceInfo {
  serviceArn: string;
  serviceName: string;
  clusterArn: string;
  status: string;
  desiredCount: number;
  runningCount: number;
  pendingCount: number;
  launchType?: string;
  taskDefinition: string;
}

export interface EcsContainerInstance {
  containerInstanceArn: string;
  ec2InstanceId: string;
  status: string;
  runningTasksCount: number;
  pendingTasksCount: number;
  registeredAt?: string;
  agentConnected: boolean;
  versionInfo?: {
    agentVersion?: string;
    dockerVersion?: string;
  };
  attributes?: Array<{
    name: string;
    value?: string;
  }>;
}

export interface EcsClusterDetails extends EcsClusterInfo {
  tasks: EcsTaskInfo[];
  services: EcsServiceInfo[];
  containerInstances?: EcsContainerInstance[];
}

/**
 * Response from terminating an ECS container instance with network cleanup info
 */
export interface EcsTerminateInstanceResponse {
  success: boolean;
  message: string;
  instanceId: string;
  terminated: boolean;
  networkCleanup: {
    attempted: boolean;
    vpcId?: string;
    success: boolean;
    message: string;
    sharedWithOtherInstances?: boolean;
  };
}

/**
 * Response from bulk terminating ECS container instances
 */
export interface EcsTerminateBulkResponse {
  success: boolean;
  message: string;
  terminated: string[];
  failed: string[];
}

export const ecsApi = {
  // List all GPU clusters
  listClusters: () =>
    api.get<{ clusters: EcsClusterInfo[] }>('/api/v1/infrastructure/ecs/clusters').then((r) => r.data),

  // Get cluster details including tasks and services
  getClusterDetails: (clusterName: string) =>
    api.get<EcsClusterDetails | null>(`/api/v1/infrastructure/ecs/clusters/${clusterName}`).then((r) => r.data),

  // List tasks in a cluster
  listTasks: (clusterName: string) =>
    api.get<{ tasks: EcsTaskInfo[] }>(`/api/v1/infrastructure/ecs/clusters/${clusterName}/tasks`).then((r) => r.data),

  // List services in a cluster
  listServices: (clusterName: string) =>
    api.get<{ services: EcsServiceInfo[] }>(`/api/v1/infrastructure/ecs/clusters/${clusterName}/services`).then((r) => r.data),

  // Scale the cluster
  scaleCluster: (clusterName: string, desiredCapacity: number) =>
    api.post<{ success: boolean; message: string; desiredCapacity: number }>(
      `/api/v1/infrastructure/ecs/clusters/${clusterName}/scale`,
      { desiredCapacity }
    ).then((r) => r.data),

  // Run a new task
  runTask: (clusterName: string, options?: { taskDefinition?: string; count?: number; command?: string[] }) =>
    api.post<{ success: boolean; message: string; tasks: EcsTaskInfo[] }>(
      `/api/v1/infrastructure/ecs/clusters/${clusterName}/tasks`,
      options || {}
    ).then((r) => r.data),

  // Stop a task
  stopTask: (clusterName: string, taskArn: string, reason?: string) =>
    api.post<{ success: boolean; message: string }>(
      '/api/v1/infrastructure/ecs/tasks/stop',
      { clusterName, taskArn, reason }
    ).then((r) => r.data),

  // Repair an incomplete ECS cluster deployment
  repairCluster: (clusterName: string, instanceType: string) =>
    api.post<{ success: boolean; message: string }>(
      `/api/v1/infrastructure/ecs/clusters/${clusterName}/repair`,
      { instanceType }
    ).then((r) => r.data),

  // Update task definition with latest PyTorch image
  updateTaskDefinition: (clusterName: string, instanceType?: string) =>
    api.post<{ success: boolean; message: string; taskDefinitionArn: string }>(
      `/api/v1/infrastructure/ecs/clusters/${clusterName}/update-task-definition`,
      { instanceType: instanceType || 'g4dn.xlarge' }
    ).then((r) => r.data),

  // Terminate a specific container instance
  terminateInstance: (clusterName: string, instanceId: string) =>
    api.post<EcsTerminateInstanceResponse>(
      `/api/v1/infrastructure/ecs/clusters/${clusterName}/instances/${instanceId}/terminate`,
      {},
      { timeout: 180000 } // 3 minutes for instance termination and network cleanup
    ).then((r) => r.data),

  // Terminate multiple container instances
  terminateInstances: (clusterName: string, instanceIds: string[]) =>
    api.post<EcsTerminateBulkResponse>(
      `/api/v1/infrastructure/ecs/clusters/${clusterName}/instances/terminate-bulk`,
      { instanceIds },
      { timeout: 180000 } // 3 minutes for instance termination and network cleanup
    ).then((r) => r.data),

  // Delete an ECS cluster (async operation - returns immediately)
  deleteCluster: (clusterName: string) =>
    api.delete<{ 
      success: boolean; 
      message: string; 
      operationId?: string; 
      executionId?: string;
      async?: boolean;
      logs?: string;
    }>(
      `/api/v1/infrastructure/ecs/clusters/${clusterName}`
    ).then((r) => r.data),

  // Get task history including stopped tasks
  getTasksHistory: (clusterName: string) =>
    api.get<{ tasks: (EcsTaskInfo & { stoppedAt?: string; stoppedReason?: string })[] }>(
      `/api/v1/infrastructure/ecs/clusters/${clusterName}/tasks/history`
    ).then((r) => r.data),

  // Get detailed task info including containers and stop reason
  getTaskDetails: (clusterName: string, taskId: string) =>
    api.get<EcsTaskInfo & {
      containers?: Array<{
        name: string;
        lastStatus: string;
        exitCode?: number;
        reason?: string;
        healthStatus?: string;
      }>;
      failures?: Array<{ arn?: string; reason?: string; detail?: string }>;
    }>(`/api/v1/infrastructure/ecs/clusters/${clusterName}/tasks/${taskId}/details`).then((r) => r.data),

  // Get task logs with enhanced error info
  getTaskLogs: (clusterName: string, taskId: string) =>
    api.get<{
      taskId: string;
      logs: string;
      logEvents: Array<{ timestamp: number; message: string }>;
      taskStatus?: string;
      stoppedReason?: string;
      containers?: Array<{
        name: string;
        lastStatus: string;
        exitCode?: number;
        reason?: string;
      }>;
      failures?: Array<{ reason?: string; detail?: string }>;
    }>(`/api/v1/infrastructure/ecs/clusters/${clusterName}/tasks/${taskId}/logs`).then((r) => r.data),
};

// ============================================
// User Profile API
// ============================================

export interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  company?: string;
  jobTitle?: string;
  phone?: string;
  bio?: string;
  avatarUrl?: string;
  timezone?: string;
  dateFormat?: string;
  role: string;
  twoFactorEnabled: boolean;
  emailVerified: boolean;
  theme?: 'dark' | 'light' | 'system';
  compactMode?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  displayName?: string;
  company?: string;
  jobTitle?: string;
  phone?: string;
  bio?: string;
  timezone?: string;
  dateFormat?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface TwoFactorSetupResponse {
  secret: string;
  qrCodeUrl: string;
  recoveryCodes: string[];
}

export interface UserSession {
  id: string;
  userId: string;
  device: string;
  browser: string;
  os: string;
  ip: string;
  location?: string;
  lastActive: string;
  createdAt: string;
  isCurrent: boolean;
}

export interface LoginHistoryEntry {
  id: string;
  timestamp: string;
  device: string;
  browser: string;
  os: string;
  ip: string;
  location?: string;
  status: 'success' | 'failed';
  failureReason?: string;
}

export interface NotificationPreferences {
  jobCompletionAlerts: boolean;
  costAlerts: boolean;
  securityAlerts: boolean;
  productUpdates: boolean;
  weeklyDigest: boolean;
  costAlertThreshold?: number;
  theme?: 'dark' | 'light' | 'system';
  compactMode?: boolean;
}

export interface UsageStats {
  totalJobs: number;
  completedJobs: number;
  failedJobs: number;
  cancelledJobs: number;
  totalSpendUsd: number;
  totalGpuHours: number;
  currentMonthSpendUsd: number;
  currentMonthGpuHours: number;
  spendingByInstanceType: Record<string, number>;
  jobsByStatus: Record<string, number>;
}

// User Profile API functions
export const profileApi = {
  // Get current user profile
  getProfile: () =>
    api.get<UserProfile>('/api/v1/profile').then((r) => r.data),

  // Update profile
  updateProfile: (data: UpdateProfileRequest) =>
    api.patch<UserProfile>('/api/v1/profile', data).then((r) => r.data),

  // Upload avatar
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post<{ avatarUrl: string }>('/api/v1/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },

  // Delete avatar
  deleteAvatar: () =>
    api.delete('/api/v1/profile/avatar'),

  // Change password
  changePassword: (data: ChangePasswordRequest) =>
    api.post<{ message: string }>('/api/v1/profile/password', data).then((r) => r.data),

  // Get usage stats
  getUsageStats: () =>
    api.get<UsageStats>('/api/v1/profile/usage').then((r) => r.data),

  // Get notification preferences
  getNotificationPreferences: () =>
    api.get<NotificationPreferences>('/api/v1/profile/notifications').then((r) => r.data),

  // Update notification preferences
  updateNotificationPreferences: (data: Partial<NotificationPreferences>) =>
    api.patch<NotificationPreferences>('/api/v1/profile/notifications', data).then((r) => r.data),

  // Export user data (GDPR compliance)
  exportData: () =>
    api.post<{ message: string; downloadUrl?: string }>('/api/v1/profile/export').then((r) => r.data),

  // Delete account
  deleteAccount: (password: string) =>
    api.delete('/api/v1/profile', { data: { password } }),
};

// Two-Factor Authentication API
export const twoFactorApi = {
  // Get 2FA status
  getStatus: () =>
    api.get<{ enabled: boolean; setupAt?: string }>('/api/v1/profile/2fa/status').then((r) => r.data),

  // Initialize 2FA setup
  initSetup: () =>
    api.post<TwoFactorSetupResponse>('/api/v1/profile/2fa/setup').then((r) => r.data),

  // Verify and enable 2FA
  verifyAndEnable: (code: string) =>
    api.post<{ message: string; recoveryCodes: string[] }>('/api/v1/profile/2fa/verify', { code }).then((r) => r.data),

  // Disable 2FA
  disable: (password: string, code: string) =>
    api.post<{ message: string }>('/api/v1/profile/2fa/disable', { password, code }).then((r) => r.data),

  // Regenerate recovery codes
  regenerateRecoveryCodes: (password: string) =>
    api.post<{ recoveryCodes: string[] }>('/api/v1/profile/2fa/recovery-codes', { password }).then((r) => r.data),
};

// Session Management API
export const sessionsApi = {
  // List all active sessions
  list: () =>
    api.get<UserSession[]>('/api/v1/profile/sessions').then((r) => r.data),

  // Revoke a specific session
  revoke: (sessionId: string) =>
    api.delete<{ message: string }>(`/api/v1/profile/sessions/${sessionId}`).then((r) => r.data),

  // Revoke all sessions except current
  revokeAll: () =>
    api.post<{ message: string; revokedCount: number }>('/api/v1/profile/sessions/revoke-all').then((r) => r.data),

  // Get login history
  getLoginHistory: (limit = 20, offset = 0) =>
    api.get<{ data: LoginHistoryEntry[]; total: number }>('/api/v1/profile/login-history', {
      params: { limit, offset },
    }).then((r) => r.data),
};

// ============================================
// Admin User Management API
// ============================================

export interface AdminUserDetail {
  id: string;
  email: string;
  role: 'admin' | 'user' | 'client';
  displayName: string | null;
  company: string | null;
  jobTitle: string | null;
  phone: string | null;
  bio: string | null;
  avatarUrl: string | null;
  timezone: string;
  dateFormat: string;
  twoFactorEnabled: boolean;
  emailVerified: boolean;
  isActivated: boolean;
  suspended: boolean;
  suspendedAt?: string;
  theme: 'dark' | 'light' | 'system';
  compactMode: boolean;
  createdAt: string;
  updatedAt: string;
  source?: 'client-dashboard';
  subscription?: { plan: string; status: string } | null;
  lastLoginAt?: string | null;
}

export interface ClientUserDetail {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  isActivated: boolean;
  emailVerified: boolean;
  suspended: boolean;
  twoFactorEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  subscription: { plan: string; status: string } | null;
  source: 'client-dashboard';
}

export interface AdminUsersResponse {
  admins: AdminUserDetail[];
  users: AdminUserDetail[];
  clients: ClientUserDetail[];
}

export const adminUsersApi = {
  // Get all users with rich detail (admin-only)
  getAllUsers: () =>
    api.get<AdminUsersResponse>('/api/v1/auth/users').then((r) => r.data),

  // Get detailed user info for audit (admin-only)
  getUserDetail: (userId: string) =>
    api.get<AdminUserDetail>(`/api/v1/auth/users/${userId}`).then((r) => r.data),

  // Delete a user (admin-only)
  deleteUser: (userId: string) =>
    api.delete<{ message: string }>(`/api/v1/auth/users/${userId}`).then((r) => r.data),

  // Suspend a user (admin-only)
  suspendUser: (userId: string) =>
    api.patch<{ message: string }>(`/api/v1/auth/users/${userId}/suspend`).then((r) => r.data),

  // Unsuspend a user (admin-only)
  unsuspendUser: (userId: string) =>
    api.patch<{ message: string }>(`/api/v1/auth/users/${userId}/unsuspend`).then((r) => r.data),

  // Reset a user's password (admin-only)
  resetPassword: (userId: string, newPassword: string) =>
    api.post<{ message: string }>(`/api/v1/auth/users/${userId}/reset-password`, {
      newPassword,
    }).then((r) => r.data),

  // Change user role (admin-only)
  changeRole: (userId: string, role: 'admin' | 'user') =>
    api.patch<{ message: string }>(`/api/v1/auth/users/${userId}/role`, { role }).then((r) => r.data),
};

// ============================================
// Network Infrastructure API (VPC, Subnets, Security Groups, IAM)
// ============================================

export interface NetworkInfraStatus {
  isProvisioned: boolean;
  isDestroying?: boolean;  // True when destroy operation is in progress
  vpcId?: string;
  subnetId?: string;
  internetGatewayId?: string;
  routeTableId?: string;
  securityGroupId?: string;
  gpuInstanceRoleArn?: string;
  gpuInstanceProfileArn?: string;
  backendExecutionRoleArn?: string;
  accountId?: string;
  region?: string;
  lastChecked?: string;
  error?: string;
}

export interface NetworkProvisionConfig {
  vpcCidr?: string;
  subnetCidr?: string;
  availabilityZone?: string;
  allowedSshCidrs?: string[];
  allowedAppCidrs?: string[];
}

export interface NetworkProvisionResult {
  success: boolean;
  status: NetworkInfraStatus;
  logs: string[];
  error?: string;
}

// Network Infrastructure API functions (one-click VPC/subnet/SG/IAM provisioning)
export const networkApi = {
  // Get current network infrastructure status
  getStatus: () =>
    api.get<NetworkInfraStatus>('/api/v1/infrastructure/status').then((r) => r.data),

  // Provision all network infrastructure (VPC, subnet, IGW, route table, security group, IAM roles)
  provision: (config?: NetworkProvisionConfig) =>
    api.post<NetworkProvisionResult>('/api/v1/infrastructure/provision', config || {}, {
      timeout: 300000, // 5 minutes for resource provisioning (matches nginx timeout)
    }).then((r) => r.data),

  // Destroy all network infrastructure (with extended timeout due to AWS resource cleanup)
  destroy: () =>
    api.delete<NetworkProvisionResult>('/api/v1/infrastructure/destroy', {
      timeout: 300000, // 5 minutes for resource cleanup (matches nginx timeout)
    }).then((r) => r.data),
};

// ============================================
// Detailed Network Infrastructure API (Live Data)
// ============================================

export interface VpcDetails {
  vpcId: string;
  cidrBlock: string;
  state: string;
  isDefault: boolean;
  enableDnsHostnames?: boolean;
  enableDnsSupport?: boolean;
  instanceTenancy?: string;
  ownerId?: string;
  tags: Record<string, string>;
}

export interface SubnetDetails {
  subnetId: string;
  vpcId: string;
  cidrBlock: string;
  availabilityZone: string;
  availabilityZoneId?: string;
  state: string;
  availableIpAddressCount: number;
  mapPublicIpOnLaunch: boolean;
  defaultForAz: boolean;
  ownerId?: string;
  tags: Record<string, string>;
}

export interface SecurityGroupRule {
  protocol: string;
  fromPort?: number;
  toPort?: number;
  ipRanges: Array<{
    cidr: string;
    description?: string;
  }>;
  ipv6Ranges: Array<{
    cidr: string;
    description?: string;
  }>;
  prefixListIds: string[];
  securityGroupReferences: Array<{
    groupId: string;
    description?: string;
  }>;
}

export interface SecurityGroupDetails {
  groupId: string;
  groupName: string;
  description: string;
  vpcId?: string;
  ownerId?: string;
  inboundRules: SecurityGroupRule[];
  outboundRules: SecurityGroupRule[];
  tags: Record<string, string>;
}

export interface InternetGatewayDetails {
  internetGatewayId: string;
  ownerId?: string;
  attachments: Array<{
    vpcId: string;
    state: string;
  }>;
  tags: Record<string, string>;
}

export interface RouteDetails {
  destinationCidrBlock?: string;
  destinationIpv6CidrBlock?: string;
  destinationPrefixListId?: string;
  gatewayId?: string;
  instanceId?: string;
  natGatewayId?: string;
  networkInterfaceId?: string;
  vpcPeeringConnectionId?: string;
  state: string;
  origin: string;
}

export interface RouteTableDetails {
  routeTableId: string;
  vpcId: string;
  ownerId?: string;
  routes: RouteDetails[];
  associations: Array<{
    associationId: string;
    subnetId?: string;
    gatewayId?: string;
    main: boolean;
    state: string;
  }>;
  tags: Record<string, string>;
}

export interface IamPolicyDetails {
  policyName: string;
  policyArn?: string;
  policyType: 'managed' | 'inline';
  document?: Record<string, unknown>;
}

export interface IamRoleDetails {
  roleName: string;
  roleArn: string;
  path: string;
  createDate?: string;
  description?: string;
  maxSessionDuration?: number;
  assumeRolePolicyDocument?: Record<string, unknown>;
  attachedPolicies: IamPolicyDetails[];
  inlinePolicies: IamPolicyDetails[];
  tags: Record<string, string>;
}

export interface IamInstanceProfileDetails {
  instanceProfileName: string;
  instanceProfileArn: string;
  instanceProfileId: string;
  path: string;
  createDate?: string;
  roles: Array<{
    roleName: string;
    roleArn: string;
  }>;
  tags: Record<string, string>;
}

export interface DetailedNetworkStatus {
  isProvisioned: boolean;
  accountId?: string;
  region?: string;
  // Arrays to show ALL resources in the AWS account
  vpcs: VpcDetails[];
  subnets: SubnetDetails[];
  internetGateways: InternetGatewayDetails[];
  routeTables: RouteTableDetails[];
  securityGroups: SecurityGroupDetails[];
  iamRoles: IamRoleDetails[];
  instanceProfiles: IamInstanceProfileDetails[];
  lastUpdated: string;
  error?: string;
}

export interface AddSecurityGroupRuleRequest {
  direction: 'inbound' | 'outbound';
  protocol: string;
  fromPort: number;
  toPort: number;
  cidrIp?: string;
  cidrIpv6?: string;
  sourceSecurityGroupId?: string;
  description?: string;
}

export interface RemoveSecurityGroupRuleRequest {
  direction: 'inbound' | 'outbound';
  protocol: string;
  fromPort: number;
  toPort: number;
  cidrIp?: string;
  cidrIpv6?: string;
  sourceSecurityGroupId?: string;
}

// ============================================
// Detailed Network API
// ============================================

export const detailedNetworkApi = {
  // Get detailed network infrastructure status with live data
  getDetailedStatus: () =>
    api.get<DetailedNetworkStatus>('/api/v1/network/detailed').then((r) => r.data),

  // Add a security group rule
  addSecurityGroupRule: (groupId: string, rule: AddSecurityGroupRuleRequest) =>
    api.post<{ success: boolean; message: string }>(
      `/api/v1/network/security-groups/${groupId}/rules`,
      rule
    ).then((r) => r.data),

  // Remove a security group rule
  removeSecurityGroupRule: (groupId: string, rule: RemoveSecurityGroupRuleRequest) =>
    api.delete<{ success: boolean; message: string }>(
      `/api/v1/network/security-groups/${groupId}/rules`,
      { data: rule }
    ).then((r) => r.data),

  // Update resource tags
  updateResourceTags: (resourceId: string, tags: Record<string, string>) =>
    api.patch<{ success: boolean; message: string }>(
      `/api/v1/network/resources/${resourceId}/tags`,
      { tags }
    ).then((r) => r.data),
};

// ============================================
// Executions API (Operation Tracking)
// ============================================

export interface Execution {
  id: string;
  userId: string;
  userEmail: string;
  type: 'instance_creation' | 'instance_deletion' | 'network_provision' | 'network_destroy' | 'fleet_start' | 'fleet_stop' | 'fleet_terminate' | 'ecs_cluster_delete' | 'ecs_instance_terminate';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  title: string;
  description?: string;
  progress: number;
  currentStep?: string;
  steps?: Array<{
    label: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    progress?: number;
    startedAt?: string;
    completedAt?: string;
    error?: string;
  }>;
  resourceIds?: {
    vpcId?: string;
    subnetIds?: string[];
    securityGroupIds?: string[];
    instanceId?: string;
    instanceIds?: string[];
    clusterName?: string;
    jobId?: string;
  };
  logs?: string;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
}

export const executionsApi = {
  // Get all executions
  list: (limit = 50) =>
    api.get<Execution[]>(`/api/v1/executions?limit=${limit}`).then((r) => r.data),

  // Get active executions only
  listActive: () =>
    api.get<Execution[]>('/api/v1/executions?active=true').then((r) => r.data),

  // Get recent executions
  getRecent: (hours = 24) =>
    api.get<Execution[]>(`/api/v1/executions/recent?hours=${hours}`).then((r) => r.data),

  // Get specific execution
  get: (id: string) =>
    api.get<Execution>(`/api/v1/executions/${id}`).then((r) => r.data),

  // Get execution stats
  getStats: () =>
    api.get<{ total: number; running: number; completed: number; failed: number }>('/api/v1/executions/stats').then((r) => r.data),

  // Cancel execution
  cancel: (id: string) =>
    api.post<{ success: boolean; execution: Execution }>(`/api/v1/executions/${id}/cancel`).then((r) => r.data),
};

// ============================================
// Operations API (New Hierarchical Model)
// ============================================

export type OperationType =
  | 'gpu_instance_create'
  | 'gpu_instance_terminate'
  | 'gpu_instance_start'
  | 'gpu_instance_stop'
  | 'gpu_instance_resize'
  | 'fleet_scale_up'
  | 'fleet_scale_down'
  | 'fleet_start'
  | 'fleet_stop'
  | 'fleet_terminate'
  | 'network_provision'
  | 'network_destroy'
  | 'network_modify_security'
  | 'vpc_attach'
  | 'vpc_detach'
  | 'ecs_cluster_create'
  | 'ecs_cluster_delete'
  | 'ecs_task_run'
  | 'ecs_task_stop'
  | 'ecs_instance_terminate'
  // GPU Container Operations
  | 'gpu_container_create'
  | 'gpu_container_start'
  | 'gpu_container_stop'
  | 'gpu_container_delete'
  | 'gpu_container_restart'
  | 'gpu_container_scale_up'
  | 'gpu_container_scale_down'
  | 'gpu_container_autoscale'
  | 'gpu_container_schedule'
  | 'gpu_container_reschedule'
  | 'gpu_container_migrate'
  | 'gpu_container_evict'
  | 'gpu_container_drain'
  | 'gpu_release'
  // Recovery
  | 'instance_repair'
  | 'orphan_cleanup'
  | 'stuck_resource_recovery'
  | 'container_recovery';

export type OperationStatus =
  | 'queued'
  | 'running'
  | 'blocked'
  | 'partial'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'rolled_back';

export type TriggerSource =
  | 'user'
  | 'api'
  | 'autoscaler'
  | 'scheduler'
  | 'policy'
  | 'spot_handler'
  | 'health_check'
  | 'system';

export type ActionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped' | 'rolled_back';
export type ActionPlane = 'control' | 'data';
export type EventSeverity = 'debug' | 'info' | 'warning' | 'error' | 'critical';

export interface OperationTargetResources {
  instanceId?: string;
  instanceIds?: string[];
  clusterId?: string;
  clusterName?: string;
  jobId?: string;
  gpuType?: string;
  instanceType?: string;
  gpuCount?: number;
  vpcId?: string;
  subnetId?: string;
  subnetIds?: string[];
  securityGroupId?: string;
  securityGroupIds?: string[];
  eniId?: string;
  internetGatewayId?: string;
  routeTableId?: string;
  region?: string;
  availabilityZone?: string;
  // Container/Pod specifics
  containerId?: string;
  containerIds?: string[];
  podName?: string;
  podNames?: string[];
  taskArn?: string;
  taskArns?: string[];
  taskDefinitionArn?: string;
  namespace?: string;
  deploymentName?: string;
  replicaSetName?: string;
  serviceName?: string;
  // Container GPU allocation
  gpuDeviceIds?: string[];
  gpuMemoryMb?: number;
  cudaVersion?: string;
  driverVersion?: string;
  // Container image
  containerImage?: string;
  imageTag?: string;
  imageDigest?: string;
  // Scaling context
  desiredReplicas?: number;
  currentReplicas?: number;
  targetReplicas?: number;
  // Node placement
  nodeId?: string;
  nodeName?: string;
  nodePool?: string;
}

export interface BlockingFactor {
  type: 'capacity' | 'quota' | 'dependency' | 'rate_limit' | 'external' | 'internal';
  description: string;
  since: string;
  estimatedResolution?: string;
  actionable?: boolean;
  suggestedAction?: string;
}

export interface StateMismatch {
  resourceType: 'instance' | 'vpc' | 'subnet' | 'security_group' | 'eni' | 'task' | 'container' | 'pod' | 'gpu_device';
  resourceId: string;
  expectedState: string;
  actualState: string;
  detectedAt: string;
  severity: 'info' | 'warning' | 'critical';
}

export interface Operation {
  id: string;
  correlationId: string;
  parentOperationId?: string;
  userId: string;
  userEmail?: string;
  type: OperationType;
  status: OperationStatus;
  triggerSource: TriggerSource;
  triggerDetails?: string;
  title: string;
  description?: string;
  targetResources: OperationTargetResources;
  progress: number;
  currentPhase?: string;
  totalActions: number;
  completedActions: number;
  failedActions: number;
  blockingFactors?: BlockingFactor[];
  hasOrphanedResources: boolean;
  hasStateMismatch: boolean;
  stateMismatches?: StateMismatch[];
  errorMessage?: string;
  errorContext?: Record<string, unknown>;
  isRetryable: boolean;
  retryCount: number;
  maxRetries: number;
  rollbackAvailable: boolean;
  rollbackOperationId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
}

export interface OperationAction {
  id: string;
  operationId: string;
  correlationId: string;
  sequenceNumber: number;
  type: string;
  plane: ActionPlane;
  status: ActionStatus;
  label: string;
  description?: string;
  targetResourceId?: string;
  targetResourceType?: string;
  awsRequestId?: string;
  awsRequest?: Record<string, unknown>;
  awsResponse?: Record<string, unknown>;
  errorCode?: string;
  errorMessage?: string;
  isRetryable: boolean;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  dependsOnActionIds?: string[];
  isBlocking: boolean;
  rollbackAvailable: boolean;
  rollbackActionId?: string;
  metadata?: Record<string, unknown>;
}

export interface OperationEvent {
  id: string;
  operationId: string;
  correlationId: string;
  actionId?: string;
  type: string;
  severity: EventSeverity;
  message: string;
  details?: string;
  metadata?: Record<string, unknown>;
  resourceId?: string;
  resourceType?: string;
  source: 'aws' | 'system' | 'user' | 'health_check' | 'webhook' | 'scheduler';
  awsEventId?: string;
  timestamp: string;
}

export interface OperationWithDetails extends Operation {
  actions: OperationAction[];
  events: OperationEvent[];
}

// ============================================
// Operation Step Types (Network Teardown Tracking)
// ============================================

export type StepType =
  | 'ec2_terminate_instance'
  | 'ec2_wait_instance_terminated'
  | 'ec2_describe_enis'
  | 'ec2_detach_eni'
  | 'ec2_delete_eni'
  | 'ec2_wait_enis_released'
  | 'ec2_revoke_sg_ingress'
  | 'ec2_revoke_sg_egress'
  | 'ec2_delete_security_group'
  | 'ec2_delete_nat_gateway'
  | 'ec2_wait_nat_deleted'
  | 'ec2_delete_vpc_endpoints'
  | 'ec2_release_elastic_ip'
  | 'ec2_delete_subnet'
  | 'ec2_disassociate_route_table'
  | 'ec2_delete_route_table'
  | 'ec2_detach_igw'
  | 'ec2_delete_igw'
  | 'ec2_delete_vpc'
  | 'ec2_verify_vpc_deleted'
  | 'ec2_delete_keypair'
  | 'db_update_job_status'
  | 'db_clear_network_refs'
  | 'network_teardown_orchestrate'
  | 'cleanup_verification'
  // Container / Pod lifecycle steps
  | 'container_validate_request'
  | 'container_check_quota'
  | 'container_reserve_gpu'
  | 'container_pull_image'
  | 'container_create'
  | 'container_wait_created'
  | 'container_attach_gpu'
  | 'container_verify_gpu_access'
  | 'container_start'
  | 'container_wait_running'
  | 'container_drain_workload'
  | 'container_stop'
  | 'container_wait_stopped'
  | 'container_detach_gpu'
  | 'container_release_gpu'
  | 'container_delete'
  | 'container_verify_deleted'
  | 'container_cleanup_volumes'
  // Scheduling & placement steps
  | 'scheduler_select_node'
  | 'scheduler_check_affinity'
  | 'scheduler_verify_capacity'
  | 'scheduler_bind_pod'
  | 'scheduler_wait_scheduled'
  // GPU runtime readiness steps
  | 'gpu_device_plugin_check'
  | 'gpu_driver_readiness'
  | 'cuda_runtime_check'
  | 'gpu_health_check'
  | 'gpu_memory_allocation'
  // Scaling steps
  | 'scale_calculate_replicas'
  | 'scale_create_containers'
  | 'scale_delete_containers'
  | 'scale_wait_stable'
  | 'scale_verify_count'
  // Eviction steps
  | 'evict_mark_for_eviction'
  | 'evict_graceful_shutdown'
  | 'evict_force_terminate'
  | 'evict_verify_removed'
  // ECS Task specific
  | 'ecs_run_task'
  | 'ecs_wait_task_running'
  | 'ecs_stop_task'
  | 'ecs_wait_task_stopped'
  | 'ecs_deregister_task_definition'
  // K8s Pod specific
  | 'k8s_create_pod'
  | 'k8s_wait_pod_scheduled'
  | 'k8s_wait_pod_ready'
  | 'k8s_delete_pod'
  | 'k8s_wait_pod_deleted'
  | 'k8s_patch_pod';

export type StepStatus =
  | 'queued'
  | 'in_progress'
  | 'waiting_on_dependency'
  | 'retrying'
  | 'partially_complete'
  | 'failed'
  | 'completed'
  | 'skipped'
  | 'rolled_back';

export type PlaneState = 'not_issued' | 'issued' | 'confirmed' | 'mismatch' | 'failed';

export interface StepProgress {
  total: number;
  completed: number;
  failed: number;
  inProgress: number;
  items?: Array<{
    id: string;
    name: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    error?: string;
  }>;
}

export interface StepBlockingFactor {
  type: 
    | 'aws_dependency' 
    | 'resource_in_use' 
    | 'rate_limit' 
    | 'internal_error' 
    | 'timeout'
    // Container-specific blocking factors
    | 'insufficient_gpu_capacity'
    | 'gpu_device_unavailable'
    | 'image_pull_error'
    | 'scheduling_failure'
    | 'node_not_ready'
    | 'device_plugin_error'
    | 'driver_mismatch'
    | 'cuda_error'
    | 'container_crash'
    | 'oom_killed'
    | 'quota_exceeded';
  resourceType?: string;
  resourceId?: string;
  awsErrorCode?: string;
  kubernetesReason?: string;
  description: string;
  since: string;
  retryAfter?: string;
  actionable: boolean;
  suggestedAction?: string;
  // Container-specific context
  containerExitCode?: number;
  containerLogs?: string;
  gpuDeviceId?: string;
}

export interface OperationStep {
  id: string;
  operationId: string;
  correlationId: string;
  parentStepId?: string;
  sequenceNumber: number;
  type: StepType;
  status: StepStatus;
  label: string;
  description?: string;
  controlPlaneState: PlaneState;
  dataPlaneState: PlaneState;
  controlPlaneIssuedAt?: string;
  dataPlaneConfirmedAt?: string;
  targetResourceId?: string;
  targetResourceType?: string;
  targetResourceArn?: string;
  expectedState?: string;
  actualState?: string;
  lastStateCheckAt?: string;
  awsRequestId?: string;
  progress?: StepProgress;
  blockingFactors?: StepBlockingFactor[];
  errorCode?: string;
  errorMessage?: string;
  isRetryable: boolean;
  retryCount: number;
  maxRetries: number;
  nextRetryAt?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  estimatedDurationMs?: number;
  metadata?: Record<string, unknown>;
}

export interface StepWithDetails extends OperationStep {
  childSteps?: StepWithDetails[];
  hasStateMismatch: boolean;
  isOverdue: boolean;
  retryAvailable: boolean;
}

export interface OperationWithSteps extends Operation {
  steps: StepWithDetails[];
  stepStats: {
    total: number;
    completed: number;
    failed: number;
    inProgress: number;
    blocked: number;
    pending: number;
  };
}

export interface AttentionItems {
  blockedOperations: Operation[];
  stateMismatches: Array<{ operation: Operation; mismatches: StateMismatch[] }>;
  longRunning: Array<{ operation: Operation; duration: number }>;
  orphanedResources: Array<{ resourceType: string; resourceId: string; detectedAt: string }>;
  totalCount: number;
}

export interface OperationStats {
  period: string;
  total: number;
  byStatus: Record<OperationStatus, number>;
  byType: Partial<Record<OperationType, number>>;
  byTrigger: Partial<Record<TriggerSource, number>>;
  avgDurationMs: number;
  successRate: number;
  attentionRequired: number;
}

export interface OperationFilters {
  status?: OperationStatus[];
  type?: OperationType[];
  triggerSource?: TriggerSource;
  instanceId?: string;
  correlationId?: string;
  gpuType?: string;
  startDate?: string;
  endDate?: string;
  hasIssues?: boolean;
  limit?: number;
  offset?: number;
}

export const operationsApi = {
  // List operations with comprehensive filtering
  list: (filters?: OperationFilters) =>
    api.get<{ data: Operation[]; total: number; pagination: object }>('/api/v1/operations', { 
      params: {
        ...filters,
        status: filters?.status?.join(','),
        type: filters?.type?.join(','),
      } 
    }).then((r) => r.data),

  // Get active operations only
  listActive: () =>
    api.get<Operation[]>('/api/v1/operations/active').then((r) => r.data),

  // Get operation with full details (actions + events)
  get: (id: string) =>
    api.get<OperationWithDetails>(`/api/v1/operations/${id}`).then((r) => r.data),

  // Get operations by correlation ID
  getCorrelated: (correlationId: string) =>
    api.get<Operation[]>(`/api/v1/operations/correlation/${correlationId}`).then((r) => r.data),

  // Get attention-required items
  getAttention: () =>
    api.get<AttentionItems>('/api/v1/operations/attention').then((r) => r.data),

  // Get operation statistics
  getStats: (period?: '1h' | '24h' | '7d' | '30d') =>
    api.get<OperationStats>('/api/v1/operations/stats', { params: { period } }).then((r) => r.data),

  // Search operations
  search: (query: string, limit?: number) =>
    api.get<Operation[]>('/api/v1/operations/search', { params: { q: query, limit } }).then((r) => r.data),

  // Get events for an operation
  getEvents: (id: string, severity?: EventSeverity[], limit?: number) =>
    api.get<OperationEvent[]>(`/api/v1/operations/${id}/events`, { 
      params: { severity: severity?.join(','), limit } 
    }).then((r) => r.data),

  // Get timeline view
  getTimeline: (id: string) =>
    api.get<{ operation: Operation; actions: OperationAction[]; events: OperationEvent[] }>(
      `/api/v1/operations/${id}/timeline`
    ).then((r) => r.data),

  // Cancel an operation
  cancel: (id: string, reason?: string) =>
    api.post<{ success: boolean; operation: Operation | null; message?: string }>(
      `/api/v1/operations/${id}/cancel`,
      { reason }
    ).then((r) => r.data),

  // Retry a failed operation
  retry: (id: string) =>
    api.post<{ success: boolean; operation: Operation | null; message?: string }>(
      `/api/v1/operations/${id}/retry`
    ).then((r) => r.data),

  // Acknowledge an issue
  acknowledge: (id: string, issueType: string, note?: string) =>
    api.post<{ success: boolean; message: string }>(
      `/api/v1/operations/${id}/acknowledge`,
      { issueType, note }
    ).then((r) => r.data),

  // Get operation with full step hierarchy
  getWithSteps: (id: string) =>
    api.get<OperationWithSteps>(`/api/v1/operations/${id}/steps`).then((r) => r.data),

  // Get steps for an operation
  getSteps: (id: string) =>
    api.get<OperationStep[]>(`/api/v1/operations/${id}/steps/list`).then((r) => r.data),
};

// ============================================
// GPU Container Operations API
// ============================================

// Container operation types subset for filtering
export const CONTAINER_OPERATION_TYPES: OperationType[] = [
  'gpu_container_create',
  'gpu_container_start',
  'gpu_container_stop',
  'gpu_container_delete',
  'gpu_container_restart',
  'gpu_container_scale_up',
  'gpu_container_scale_down',
  'gpu_container_autoscale',
  'gpu_container_schedule',
  'gpu_container_reschedule',
  'gpu_container_migrate',
  'gpu_container_evict',
  'gpu_container_drain',
  'gpu_release',
  'container_recovery',
];

export interface ContainerOperationFilters extends OperationFilters {
  containerId?: string;
  podName?: string;
  namespace?: string;
  nodeName?: string;
  taskArn?: string;
}

export interface ContainerOperationStats extends OperationStats {
  // Container-specific stats
  byContainer: Record<string, number>;
  byNamespace: Record<string, number>;
  byNode: Record<string, number>;
  gpuUtilization: {
    allocatedGpus: number;
    totalGpus: number;
    utilizationPercent: number;
  };
  failureBreakdown: {
    imagePullFailures: number;
    schedulingFailures: number;
    gpuAllocationFailures: number;
    crashLoopBackOff: number;
    oomKilled: number;
  };
}

export interface ContainerAttentionItems extends AttentionItems {
  crashLoopContainers: Array<{ 
    operation: Operation; 
    restartCount: number;
    lastRestartReason: string;
  }>;
  pendingScheduling: Array<{
    operation: Operation;
    waitDuration: number;
    reason: string;
  }>;
  gpuAllocationIssues: Array<{
    operation: Operation;
    requestedGpus: number;
    availableGpus: number;
    nodeName?: string;
  }>;
}

export const containerOperationsApi = {
  // List container operations with comprehensive filtering
  list: (filters?: ContainerOperationFilters) =>
    api.get<{ data: Operation[]; total: number; pagination: object }>('/api/v1/container-operations', { 
      params: {
        ...filters,
        status: filters?.status?.join(','),
        type: filters?.type?.join(',') || CONTAINER_OPERATION_TYPES.join(','),
      } 
    }).then((r) => r.data),

  // Get active container operations only
  listActive: () =>
    api.get<Operation[]>('/api/v1/container-operations/active').then((r) => r.data),

  // Get container operation with full details
  get: (id: string) =>
    api.get<OperationWithDetails>(`/api/v1/container-operations/${id}`).then((r) => r.data),

  // Get container operation with steps
  getWithSteps: (id: string) =>
    api.get<OperationWithSteps>(`/api/v1/container-operations/${id}/steps`).then((r) => r.data),

  // Get attention-required items for containers
  getAttention: () =>
    api.get<ContainerAttentionItems>('/api/v1/container-operations/attention').then((r) => r.data),

  // Get container operation statistics
  getStats: (period?: '1h' | '24h' | '7d' | '30d') =>
    api.get<ContainerOperationStats>('/api/v1/container-operations/stats', { params: { period } }).then((r) => r.data),

  // Get operations by container ID
  getByContainer: (containerId: string) =>
    api.get<Operation[]>(`/api/v1/container-operations/container/${containerId}`).then((r) => r.data),

  // Get operations by pod name
  getByPod: (namespace: string, podName: string) =>
    api.get<Operation[]>(`/api/v1/container-operations/pod/${namespace}/${podName}`).then((r) => r.data),

  // Get operations by node
  getByNode: (nodeName: string) =>
    api.get<Operation[]>(`/api/v1/container-operations/node/${nodeName}`).then((r) => r.data),

  // Cancel a container operation
  cancel: (id: string, reason?: string) =>
    api.post<{ success: boolean; operation: Operation | null; message?: string }>(
      `/api/v1/container-operations/${id}/cancel`,
      { reason }
    ).then((r) => r.data),

  // Retry a failed container operation
  retry: (id: string) =>
    api.post<{ success: boolean; operation: Operation | null; message?: string }>(
      `/api/v1/container-operations/${id}/retry`
    ).then((r) => r.data),

  // Force stop a container (for stuck operations)
  forceStop: (id: string, reason?: string) =>
    api.post<{ success: boolean; operation: Operation | null; message?: string }>(
      `/api/v1/container-operations/${id}/force-stop`,
      { reason }
    ).then((r) => r.data),

  // Acknowledge GPU allocation issue
  acknowledgeGpuIssue: (id: string, resolution: 'retry' | 'skip' | 'manual') =>
    api.post<{ success: boolean; message: string }>(
      `/api/v1/container-operations/${id}/acknowledge-gpu-issue`,
      { resolution }
    ).then((r) => r.data),
};
